import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Clock, ShieldCheck, ExternalLink, Phone, Mail, Siren } from "lucide-react";
import { api } from "../lib/api";
import { resolveMedia, isUnverified } from "../lib/media";
import CampaignHelpButtons from "../components/CampaignHelpButtons";
import CampaignNotice from "../components/CampaignNotice";
import RescueShareButtons from "../components/RescueShareButtons";

const STEPS = ["Needs Help", "Rescue Underway", "Partially Safe", "All Rescued", "Closed"];

function StatusTracker({ status }) {
  const cur = STEPS.indexOf(status);
  return (
    <ol className="grid grid-cols-2 sm:grid-cols-5 gap-1" data-testid="rescue-status-tracker">
      {STEPS.map((s, i) => (
        <li key={s} className={`text-[10px] sm:text-[11px] leading-tight font-bold uppercase tracking-widest px-2 py-2 border ${i <= cur ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-400"} ${i === cur ? "!bg-red-600 !border-red-600" : ""}`}>
          {s}
        </li>
      ))}
    </ol>
  );
}

function DonationLinks({ links }) {
  return (
    <div className="border border-zinc-900 p-5" data-testid="rescue-donation-links">
      <div className="label-eyebrow inline-flex items-center gap-2"><ShieldCheck size={12} /> Donate directly to the rescue</div>
      {links.length === 0 ? (
        <p className="text-sm text-zinc-600 mt-2">No verified rescue donation links yet. We only list links after Paws on Record has verified the organization.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {links.map((d) => (
            <li key={d.url}>
              <a href={d.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 border border-zinc-300 hover:border-zinc-900 px-3 py-2" data-testid="rescue-donation-link">
                <span><b className="text-sm">{d.org_name}</b>{d.note && <span className="block text-xs text-zinc-600">{d.note}</span>}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold text-green-700 inline-flex items-center gap-1 shrink-0"><ShieldCheck size={11} /> Verified <ExternalLink size={11} /></span>
              </a>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-zinc-500 mt-3">These donations go straight to the rescue organization. Paws on Record does not receive or handle these funds.</p>
    </div>
  );
}

export default function UrgentRescueDetail() {
  const { rescueId } = useParams();
  const [params] = useSearchParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  const adminToken = params.get("preview") ? window.sessionStorage.getItem("paws_admin_token") : "";

  useEffect(() => {
    window.scrollTo(0, 0);
    const req = adminToken
      ? api.get(`/admin/urgent-rescues/${rescueId}`, { headers: { "X-Admin-Token": adminToken } })
      : api.get(`/urgent-rescues/${rescueId}`);
    req.then(({ data }) => setR(data)).catch(() => setErr(true));
  }, [rescueId, adminToken]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="rescue-not-found">
      <Siren className="mx-auto text-zinc-300 mb-3" size={32} />
      <p className="text-zinc-600">This rescue alert isn't available.</p>
      <Link to="/urgent-rescues" className="underline text-sm mt-3 inline-block">Back to Urgent Rescues</Link>
    </div>
  );
  if (!r) return <div className="max-w-5xl mx-auto px-6 py-20 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10" data-testid="urgent-rescue-detail">
      <Helmet><title>{`${r.title} — Urgent Rescue · Paws on Record`}</title><meta name="description" content={r.summary?.slice(0, 160)} /></Helmet>
      {r.is_published === false && (
        <div className="mb-5 bg-zinc-900 text-white px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="rescue-draft-preview-banner">
          <span><b className="text-amber-400">DRAFT PREVIEW</b> — only you can see this. It is not public until you click Publish in the admin dashboard.</span>
          <Link to="/admin" className="underline text-xs font-bold uppercase tracking-widest">Back to admin</Link>
        </div>
      )}
      <Link to="/urgent-rescues" className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1 hover:text-red-600" data-testid="rescue-back-link"><ArrowLeft size={12} /> All urgent rescues</Link>

      <div className="mt-5"><CampaignNotice rescue={r} /></div>

      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-5 leading-none" data-testid="rescue-title">{r.title}</h1>
      <div className="text-xs text-zinc-500 mt-3 font-mono flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1" data-testid="rescue-location"><MapPin size={11} /> {isUnverified(r) && <b className="text-amber-700 uppercase">Reported location:</b>} {r.incident_location || `${r.city}, ${r.state_code || r.state}`}{isUnverified(r) && " (not yet independently verified)"}</span>
        {r.incident_date && <span className="inline-flex items-center gap-1"><Clock size={11} /> Reported {r.incident_date}</span>}
        {isUnverified(r) && <span className="text-amber-700 font-bold uppercase" data-testid="rescue-status-unconfirmed">Current rescue status: unconfirmed</span>}
      </div>
      <div className="mt-5"><StatusTracker status={r.status} /></div>

      <div className="grid lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-7 space-y-6">
          {r.photo_url && (
            <figure className="border border-zinc-900">
              <img src={resolveMedia(r.photo_url)} alt={r.photo_caption || r.title} className="w-full max-h-[620px] object-contain bg-zinc-950" data-testid="rescue-photo" />
              <figcaption className="text-[11px] text-zinc-600 px-3 py-2 border-t border-zinc-200">
                {r.photo_caption} {r.photo_attribution && <span className="font-mono">· {r.photo_attribution}</span>}
                {r.photo_source_url && <a href={r.photo_source_url} target="_blank" rel="noreferrer" className="underline ml-1">source</a>}
              </figcaption>
            </figure>
          )}
          <div>
            <div className="label-eyebrow text-red-600">{r.animals_description}</div>
            <p className="mt-2 text-base leading-relaxed text-zinc-800 whitespace-pre-line" data-testid="rescue-summary">{r.summary}</p>
            {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs underline mt-2 inline-block">Original report</a>}
          </div>
          {r.case_updates?.length > 0 && (
            <div data-testid="rescue-updates">
              <div className="label-eyebrow mb-2">Updates</div>
              <ul className="border-l-2 border-zinc-900 pl-4 space-y-3">
                {r.case_updates.map((u, i) => <li key={`${u.date}-${i}`}><div className="text-[11px] font-mono text-zinc-500">{u.date}</div><div className="text-sm">{u.text}</div></li>)}
              </ul>
            </div>
          )}
          {r.is_published !== false && <div><div className="label-eyebrow mb-2">Share this alert</div><RescueShareButtons rescue={r} /></div>}
        </div>
        <aside className="lg:col-span-5 space-y-6">
          {(r.contact_name || r.contact_phone || r.contact_email) && (
            <div className="border border-zinc-900 p-5 text-sm space-y-1" data-testid="rescue-contact">
              <div className="label-eyebrow mb-1">On-the-ground contact</div>
              {r.contact_name && <div className="font-bold">{r.contact_name}</div>}
              {r.contact_phone && <a href={`tel:${r.contact_phone}`} className="flex items-center gap-2 underline"><Phone size={12} /> {r.contact_phone}</a>}
              {r.contact_email && <a href={`mailto:${r.contact_email}`} className="flex items-center gap-2 underline"><Mail size={12} /> {r.contact_email}</a>}
            </div>
          )}
          <div className="border-2 border-zinc-900 p-5 space-y-3" data-testid="rescue-help-box">
            <div className="font-display font-black text-xl">Can you help these animals?</div>
            <p className="text-sm text-zinc-600">Rescue organizations: contact Paws on Record. Fosters, transport volunteers and individuals: offer your help.</p>
            <CampaignHelpButtons rescue={r} disabled={r.is_published === false} testPrefix="detail" />
          </div>
          {((r.share_image_approved !== false && r.share_image_url) || r.flyer_url) && (
            <figure className="border border-zinc-900" data-testid="rescue-flyer">
              <img src={resolveMedia((r.share_image_approved !== false && r.share_image_url) || r.flyer_url)} alt={`Campaign flyer: ${r.title}`} className="w-full object-contain bg-zinc-950" />
              <figcaption className="text-[11px] text-zinc-600 px-3 py-2 border-t border-zinc-200">Campaign flyer — please share.</figcaption>
            </figure>
          )}
          <DonationLinks links={r.donation_links || []} />
        </aside>
      </div>
    </div>
  );
}
